<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { TriangleAlert } from 'lucide-vue-next';
import ScheduleDayChips from './ScheduleDayChips.vue';
import OrderingIcon from './OrderingIcon.vue';
import { SHOW_ORDERING_ICONS } from './orderingIcons';
import ScheduleDetailLine from './ScheduleDetailLine.vue';
import ScheduleQualifier from './ScheduleQualifier.vue';
import ScheduleTypeIcon from './ScheduleTypeIcon.vue';
import { playerNameFor } from '@/composables/api/usePlayerSummaries';
import {
    NEXT_DAY,
    ORDERING_CUSTOM,
    SCHEDULE_KIND_LABELS,
    kindUsesOrdering,
    orderingFromRow,
    orderingLabel,
    formatDayNames,
    positiveInteger,
    repeatWindowText,
    scheduleColor,
    scheduleTimes,
    summarizeSchedule,
    summaryText,
    type ScheduleRow,
    type ScheduleSummary,
} from '@/utils/customPlaylistSchedule';

/**
 * One schedule, drawn at one of two densities.
 *
 * `line` (mockup 3, the drawer): priority-colour rail, type glyph, day chips or
 * "Every day" (or the date for a dated row), time range, qualifier, and a muted
 * tail — the date window for a repeating row, the type for a dated one.
 *
 * `card` (mockup 4, React's eventPopoverCard): three lines. The search card and
 * the inspector draw them the same way. The actions sit in a column of their
 * own to the right of the lines.
 *   1. the dates and times, and nothing else: the days in words ("Mon Wed
 *      Fri") or the date, the time range, and a repeating row's date window
 *      with its end date. Not the list's name:
 *      React's card needs it because a zone's calendar mixes lists, but these
 *      cards sit inside one list, so every card would repeat the same name;
 *   2. the icons, the type and the qualifier ({@link ScheduleDetailLine});
 *   3. the ordering, the priority when the row holds one, and whose zone it is.
 *      The ordering's icon is drawn only when SHOW_ORDERING_ICONS is on.
 *
 * Both draw the row's silent failure modes (summary.problems) as a small
 * warning line, and dim an expired row with its dates struck through.
 *
 * The `actions` slot is where the editor stage puts Edit / View / Delete. It
 * receives `{ schedule, summary }` and shows on hover. The card's `heading`
 * slot draws a line above line one, inside the colour rail. The search card
 * puts the zone that wrote the schedule there. `embedded` draws the card
 * inside another card, the search card. It then has no border or fill of its
 * own, and no warning lines.
 */
const props = withDefaults(
    defineProps<{
        schedule: ScheduleRow;
        /** The owner zone's wall-clock time (amendment A6). */
        now: Date;
        /** Precomputed by a list that already summarised every row. */
        summary?: ScheduleSummary | null;
        density?: 'line' | 'card';
        /**
         * The card drawn inside another card, as the custom-list search card
         * does: no border or fill of its own, and no warning lines. The lines
         * themselves read the same as in the inspector.
         */
        embedded?: boolean;
        /**
         * The schedule the person came here to see, such as the one clicked on the schedule
         * search. The row is marked with data-highlighted for as long as this is true.
         */
        highlighted?: boolean;
        /**
         * Play the glow once and scroll the row into view. The parent sets it for one highlight
         * request and clears it when the row emits `glowed`. So a row that is drawn again later,
         * such as after its zone is collapsed and expanded, does not glow a second time.
         */
        glow?: boolean;
    }>(),
    { summary: null, density: 'line', embedded: false, highlighted: false, glow: false },
);

const emit = defineEmits<{
    /** The glow has started. The parent clears `glow`, so the next mount of this row does not glow. */
    glowed: [];
}>();

const rootElement = ref<HTMLElement | null>(null);

/**
 * The glow on the highlighted schedule. It runs once. The ring is drawn only while it runs, so
 * the schedule then looks like every other one, though the inspector still points at it.
 */
const GLOW_CLASSES = 'ring-primary ring-offset-background ring-2 ring-offset-1 animate-[schedule-glow_1.6s_ease-out_1]';
/** The animation's own 1.6 seconds and some slack. The glow ends here when no animationend arrives. */
const GLOW_FALLBACK_MS = 2000;

const glowing = ref(false);
let glowTimer: ReturnType<typeof setTimeout> | null = null;

function stopGlow() {
    glowing.value = false;
    if (glowTimer !== null) {
        clearTimeout(glowTimer);
        glowTimer = null;
    }
}

function onAnimationEnd(event: AnimationEvent) {
    if (event.animationName === 'schedule-glow') stopGlow();
}

// Only a change to true does anything. The parent clears `glow` as soon as the glow starts,
// and that must not cut the glow short.
watch(
    () => props.glow,
    async (glow) => {
        if (!glow) return;
        stopGlow();
        glowing.value = true;
        glowTimer = setTimeout(stopGlow, GLOW_FALLBACK_MS);
        emit('glowed');
        await nextTick();
        rootElement.value?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    },
    { immediate: true },
);
onBeforeUnmount(stopGlow);

const resolved = computed<ScheduleSummary>(
    () => props.summary ?? summarizeSchedule(props.schedule, { now: props.now, playerName: playerNameFor }),
);
const color = computed(() => scheduleColor(props.schedule));
const expired = computed(() => resolved.value.state === 'expired');
const repeating = computed(() => resolved.value.days !== null);
const kindLabel = computed(() => SCHEDULE_KIND_LABELS[resolved.value.kind]);

/**
 * A one-off with a date and no times plays the whole day. The summary moves the
 * times into the date window for a multi-day span, so check the row itself.
 */
const timeText = computed(() => {
    if (resolved.value.timeRange) return resolved.value.timeRange;
    const times = scheduleTimes(props.schedule);
    const hasTimes = Boolean(times.start || times.end);
    return resolved.value.kind === 'oneoff' && resolved.value.dateWindow && !hasTimes ? 'all day' : null;
});

/** Only a priority the row actually holds; never React's `|| 10` fallback. */
const priority = computed(() => positiveInteger(props.schedule.priority));

const lineTail = computed(() => {
    if (repeating.value) return repeatWindowText(resolved.value);
    return kindLabel.value.toLowerCase();
});

const orderingChoice = computed(() => (kindUsesOrdering(resolved.value.kind) ? orderingFromRow(props.schedule) : null));

const orderingText = computed(() => {
    if (orderingChoice.value === null) return null;
    return orderingChoice.value === ORDERING_CUSTOM ? 'Custom ordering' : orderingLabel(orderingChoice.value);
});

const cardThirdLine = computed(() =>
    [orderingText.value, priority.value !== null ? `Priority ${priority.value}` : null]
        .filter(Boolean)
        .join(' · '),
);

const repeatWindow = computed(() => repeatWindowText(resolved.value));

// The card writes its days as words, so its tooltip does too.
const title = computed(() => summaryText(resolved.value, props.density === 'card' ? { dayLabels: formatDayNames } : {}));

/**
 * Edit / View / Delete show only while the pointer is over the row, or while
 * one of them has keyboard focus, so a list of schedules reads as schedules.
 * A device with no hover (a phone) always shows them.
 */
const HOVER_ACTIONS =
    'flex shrink-0 items-center gap-2 opacity-0 transition-opacity group-hover/schedule:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100';
</script>

<template>
    <div
        v-if="density === 'line'"
        ref="rootElement"
        class="group/schedule flex items-start gap-2 rounded-md px-2.5 py-1.5"
        :class="[expired ? 'opacity-50' : '', glowing ? GLOW_CLASSES : '']"
        :data-highlighted="highlighted ? 'true' : undefined"
        :data-glowing="glowing ? 'true' : undefined"
        @animationend.self="onAnimationEnd"
        :data-schedule-id="schedule.id ?? undefined"
        data-testid="schedule-row"
        data-density="line"
        :data-state="resolved.state"
        :data-kind="resolved.kind"
        :title="title"
    >
        <span
            class="min-h-[15px] w-1 shrink-0 self-stretch rounded-sm"
            :style="{ backgroundColor: color }"
            aria-hidden="true"
            data-testid="schedule-color"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
            <div class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
                <ScheduleTypeIcon :kind="resolved.kind" />
                <ScheduleDayChips v-if="resolved.days !== null" :days="resolved.days" />
                <span
                    v-else-if="resolved.dateWindow"
                    class="tabular-nums"
                    :class="expired ? 'line-through' : ''"
                    data-testid="schedule-date"
                    >{{ resolved.dateWindow }}</span
                >
                <span v-if="timeText && resolved.days === null && resolved.dateWindow" class="text-muted-foreground/60" aria-hidden="true">·</span>
                <span v-if="timeText" class="tabular-nums" data-testid="schedule-time"
                    >{{ timeText
                    }}<span v-if="resolved.crossesMidnight" class="text-muted-foreground" data-testid="schedule-next-day">{{ ` ${NEXT_DAY}` }}</span></span
                >
                <template v-if="resolved.qualifierParts.length > 0">
                    <span class="text-muted-foreground/60" aria-hidden="true">·</span>
                    <span class="text-muted-foreground" data-testid="schedule-qualifier"><ScheduleQualifier :parts="resolved.qualifierParts" /></span>
                </template>
                <span
                    v-if="priority !== null"
                    class="text-muted-foreground rounded-[3px] border px-1 font-mono text-[10px] font-semibold tabular-nums"
                    :title="`Priority ${priority}`"
                    data-testid="schedule-priority"
                    >P{{ priority }}</span
                >
            </div>
            <p
                v-for="problem in resolved.problems"
                :key="problem"
                class="text-warning-border flex items-start gap-1 text-xs leading-snug"
                data-testid="schedule-problem"
            >
                <TriangleAlert class="mt-px size-3 shrink-0" aria-hidden="true" />
                <span>{{ problem }}</span>
            </p>
        </div>
        <span
            v-if="lineTail"
            class="text-muted-foreground shrink-0 pt-0.5 text-xs tabular-nums"
            :class="expired && repeating ? 'line-through' : ''"
            data-testid="schedule-tail"
            >{{ lineTail }}</span
        >
        <span v-if="$slots.actions" :class="HOVER_ACTIONS">
            <slot name="actions" :schedule="schedule" :summary="resolved" />
        </span>
    </div>

    <div
        v-else
        ref="rootElement"
        class="group/schedule flex overflow-hidden"
        :class="[embedded ? 'gap-1.5' : 'bg-card rounded-md border', expired ? 'opacity-60' : '', glowing ? GLOW_CLASSES : '']"
        :data-highlighted="highlighted ? 'true' : undefined"
        :data-glowing="glowing ? 'true' : undefined"
        @animationend.self="onAnimationEnd"
        :data-schedule-id="schedule.id ?? undefined"
        data-testid="schedule-row"
        data-density="card"
        :data-state="resolved.state"
        :data-kind="resolved.kind"
        :data-embedded="embedded ? 'true' : undefined"
        :title="embedded ? undefined : title"
    >
        <span
            class="shrink-0"
            :class="embedded ? 'w-1 rounded-sm' : 'w-[5px]'"
            :style="{ backgroundColor: color }"
            aria-hidden="true"
            data-testid="schedule-color"
        />
        <!--
            Two columns: the schedule's lines on the left, the actions on the right.
            The actions column spans every line, so a long date line wraps inside the left column.
        -->
        <div class="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-x-2" :class="embedded ? '' : 'px-2.5 py-1.5'">
            <div class="flex min-w-0 flex-col gap-0.5">
                <div v-if="$slots.heading" class="mb-0.5 min-w-0" data-testid="schedule-card-heading">
                    <slot name="heading" />
                </div>
                <div class="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs" data-testid="schedule-card-line-1">
                    <span v-if="resolved.days !== null" data-testid="schedule-days">{{ formatDayNames(resolved.days) }}</span>
                    <span
                        v-else-if="resolved.dateWindow"
                        class="tabular-nums"
                        :class="expired ? 'line-through' : ''"
                        data-testid="schedule-date"
                        >{{ resolved.dateWindow }}</span
                    >
                    <!-- A dated row with no date and no times still says so, rather than leaving the line empty. -->
                    <span v-else-if="!timeText" class="text-muted-foreground/70" data-testid="schedule-date">no date</span>
                    <span v-if="timeText" class="tabular-nums" data-testid="schedule-time"
                        >{{ timeText }}<span v-if="resolved.crossesMidnight" class="text-muted-foreground/70" data-testid="schedule-next-day">{{ ` ${NEXT_DAY}` }}</span></span
                    >
                    <!-- The dot wraps with the window, so it never dangles at the end of the line above. -->
                    <span v-if="repeatWindow" class="flex items-center gap-x-1.5 whitespace-nowrap">
                        <span class="text-muted-foreground/60" aria-hidden="true">·</span>
                        <span
                            class="tabular-nums"
                            :class="expired ? 'line-through' : ''"
                            data-testid="schedule-repeat-window"
                            >{{ repeatWindow }}</span
                        >
                    </span>
                </div>
                <ScheduleDetailLine :summary="resolved" data-testid="schedule-card-line-2" />
                <div v-if="cardThirdLine" class="text-muted-foreground flex items-start gap-1.5 text-xs" data-testid="schedule-card-line-3">
                    <!-- The ordering's icon, the same one the editor's Ordering menu shows beside it. Hidden for now. -->
                    <span v-if="SHOW_ORDERING_ICONS && orderingChoice !== null" class="flex h-4 shrink-0 items-center">
                        <OrderingIcon :choice="orderingChoice" class="size-3.5" />
                    </span>
                    <span class="min-w-0 leading-4">{{ cardThirdLine }}</span>
                </div>
                <p
                    v-for="problem in embedded ? [] : resolved.problems"
                    :key="problem"
                    class="text-warning-border flex items-start gap-1 text-xs leading-snug"
                    data-testid="schedule-problem"
                >
                    <TriangleAlert class="mt-px size-3 shrink-0" aria-hidden="true" />
                    <span>{{ problem }}</span>
                </p>
            </div>
            <span v-if="$slots.actions" class="self-start" :class="HOVER_ACTIONS">
                <slot name="actions" :schedule="schedule" :summary="resolved" />
            </span>
        </div>
    </div>
</template>
